// Blender-style navigation gizmo: a coordinate cross pinned in the bottom-right
// corner of the desktop viewport that mirrors the main camera's orientation.
// Six axis balls (+X/+Y/+Z solid + labelled, −X/−Y/−Z dimmed) are clickable —
// clicking one aligns the view down that axis. Desktop only (hidden in XR).
//
// Lives in its own tiny scene rendered by the overlay pass with its own ortho
// camera and a fixed pixel viewport in the corner.

import * as THREE from "three";

const gizmoScene = new THREE.Scene();
export const GIZMO_SIZE = 140; // pixels (square viewport)
const FRUSTUM = 1.7;
const gizmoCamera = new THREE.OrthographicCamera(
  -FRUSTUM,
  FRUSTUM,
  FRUSTUM,
  -FRUSTUM,
  0.1,
  10,
);
gizmoCamera.position.set(0, 0, 3);
gizmoCamera.lookAt(0, 0, 0);

const BALL_POS = 1.0; // ball center distance from the origin
// All balls share one radius — negatives are distinguished by dimming, not size,
// so a negative axis facing you doesn't look smaller than the side balls (the
// ortho camera applies no perspective foreshortening to even it out).
const POS_BALL_R = 0.34;
const NEG_BALL_R = 0.34;
const HOVER_SCALE = 1.35;

interface AxisDef {
  dir: THREE.Vector3;
  color: number;
  label: string;
}

// Clickable axis balls; userData.axisDir = the world axis to look down.
const balls: THREE.Mesh[] = [];

function dim(hex: number, f: number): number {
  return new THREE.Color(hex).multiplyScalar(f).getHex();
}

function makeLabelSprite(letter: string, negative: boolean): THREE.Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // The axis letter keeps the SAME size across all balls. For a negative axis the
  // minus is a smaller mark, and the minus + letter are centered as one unit.
  const LF = "bold 78px system-ui, sans-serif";
  const MF = "bold 42px system-ui, sans-serif";
  ctx.font = LF;
  const lw = ctx.measureText(letter).width;
  if (negative) {
    ctx.font = MF;
    const mw = ctx.measureText("−").width;
    const gap = 6;
    ctx.font = LF;
    ctx.fillText(letter, 64 + (mw + gap) / 2, 68); // letter right of center
    ctx.font = MF;
    ctx.fillText("−", 64 - (lw + gap) / 2, 68); // minus left of center
  } else {
    ctx.fillText(letter, 64, 68);
  }
  const tex = new THREE.CanvasTexture(canvas);
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: tex, depthTest: false }),
  );
  sprite.scale.set(0.55, 0.55, 1);
  return sprite;
}

function createGizmo(): THREE.Group {
  const group = new THREE.Group();
  const axes: AxisDef[] = [
    { dir: new THREE.Vector3(1, 0, 0), color: 0xe74c3c, label: "X" },
    { dir: new THREE.Vector3(0, 1, 0), color: 0x2ecc71, label: "Y" },
    { dir: new THREE.Vector3(0, 0, 1), color: 0x3498db, label: "Z" },
  ];

  for (const { dir, color, label } of axes) {
    // Colored line from the center to the NEAR EDGE of the positive ball (so it
    // doesn't run into the bubble).
    const lineLen = BALL_POS - POS_BALL_R;
    const line = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.04, lineLen, 8),
      new THREE.MeshBasicMaterial({ color }),
    );
    line.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    line.position.copy(dir.clone().multiplyScalar(lineLen / 2));
    group.add(line);

    // Positive ball — solid, with a white axis letter on top.
    const posBall = new THREE.Mesh(
      new THREE.SphereGeometry(POS_BALL_R, 18, 18),
      new THREE.MeshBasicMaterial({ color, transparent: true }),
    );
    posBall.position.copy(dir.clone().multiplyScalar(BALL_POS));
    posBall.userData.axisDir = dir.clone();
    group.add(posBall);
    balls.push(posBall);

    const sprite = makeLabelSprite(label, false);
    sprite.position.copy(posBall.position);
    sprite.renderOrder = 1;
    group.add(sprite);
    posBall.userData.label = sprite; // so applyFacing can hide it when behind

    // Negative ball — dimmed; its "-X/-Y/-Z" label shows only on hover.
    const negBall = new THREE.Mesh(
      new THREE.SphereGeometry(NEG_BALL_R, 18, 18),
      new THREE.MeshBasicMaterial({ color: dim(color, 0.6), transparent: true }),
    );
    negBall.position.copy(dir.clone().multiplyScalar(-BALL_POS));
    negBall.userData.axisDir = dir.clone().multiplyScalar(-1);
    const negSprite = makeLabelSprite(label, true);
    negSprite.position.copy(negBall.position);
    negSprite.renderOrder = 1;
    negSprite.visible = false;
    group.add(negSprite);
    negBall.userData.label = negSprite;
    negBall.userData.labelHoverOnly = true;
    group.add(negBall);
    balls.push(negBall);
  }

  // Center hub.
  group.add(
    new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 12, 12),
      new THREE.MeshBasicMaterial({ color: 0x888888 }),
    ),
  );
  return group;
}

const gizmo = createGizmo();
gizmoScene.add(gizmo);
gizmoScene.add(new THREE.AmbientLight(0xffffff, 2));

// Background disc.
const gizmoBg = new THREE.Mesh(
  new THREE.CircleGeometry(1.55, 32),
  new THREE.MeshBasicMaterial({ color: 0x111122, transparent: true, opacity: 0.7 }),
);
gizmoBg.position.z = -0.1;
gizmoScene.add(gizmoBg);

// The gizmo is pure UI but shares the renderer, so the presentation rig's
// ACES tone mapping would otherwise tint it. Everything is in the scene by
// now — opt every material out in one sweep.
gizmoScene.traverse((o) => {
  const m = (o as THREE.Mesh).material as THREE.Material | undefined;
  if (m) m.toneMapped = false;
});

// ── Facing (depth) ──────────────────────────────────────────────────────────
// Each frame, per ball: dim/fade the ones facing away, hide their labels, and
// flag whether they're front-facing (only front balls are pickable). This is
// what makes the ball pointing at you OCCLUDE the one behind it — visually and
// for clicks — instead of the back ball peeking out and being grabbable.
// An axis the caller has hidden for the current context (layout mode hides −Y:
// no one designs a floor plan from underneath). Modeling leaves this null so all
// six balls show. Matched by direction so the ball is dropped from view + picks.
let hiddenAxis: THREE.Vector3 | null = null;
export function setGizmoHiddenAxis(dir: THREE.Vector3 | null): void {
  hiddenAxis = dir ? dir.clone().normalize() : null;
}

const _facingV = new THREE.Vector3();
function applyFacing(): void {
  for (const ball of balls) {
    // Hidden axis (e.g. −Y in layout): drop the ball from rendering + picking.
    const dir = ball.userData.axisDir as THREE.Vector3;
    if (hiddenAxis && dir.dot(hiddenAxis) > 0.9) {
      ball.visible = false;
      ball.userData.front = false; // unpickable
      const lbl = ball.userData.label as THREE.Sprite | undefined;
      if (lbl) lbl.visible = false;
      continue;
    }
    ball.visible = true;
    // Ball position rotated into camera-view space; +z = toward the camera.
    _facingV.copy(ball.position).applyQuaternion(gizmo.quaternion);
    const zn = _facingV.z / BALL_POS; // +1 dead ahead, −1 directly behind
    ball.userData.front = zn > -0.2; // not clearly behind → pickable
    (ball.material as THREE.MeshBasicMaterial).opacity =
      0.3 + 0.7 * Math.min(1, Math.max(0, (zn + 1) / 2));
    const label = ball.userData.label as THREE.Sprite | undefined;
    if (label) {
      label.visible = ball.userData.labelHoverOnly
        ? (ball === hovered && zn > -0.2) || zn > 0.6 // neg: hover OR facing you
        : zn > -0.2; // positive: visible unless clearly behind. NOT `> 0`, or an
      // axis perpendicular to the view (zn≈0, e.g. an ortho side view) sits right
      // on the boundary and the label flickers on/off as it wobbles past zero.
    }
  }
}

// ── Picking + hover ────────────────────────────────────────────────────────
let hovered: THREE.Mesh | null = null;
const _raycaster = new THREE.Raycaster();
const _ndc = new THREE.Vector2();

// Is the client-space point inside the corner gizmo viewport?
export function isInsideGizmo(clientX: number, clientY: number): boolean {
  return (
    clientX >= window.innerWidth - GIZMO_SIZE &&
    clientY >= window.innerHeight - GIZMO_SIZE
  );
}

// Fill _ndc with the gizmo-viewport-local NDC for a client point; returns
// false if the point isn't inside the gizmo region.
function ndcInGizmo(clientX: number, clientY: number): boolean {
  const lx = clientX - (window.innerWidth - GIZMO_SIZE);
  const ly = clientY - (window.innerHeight - GIZMO_SIZE); // top of region
  if (lx < 0 || lx > GIZMO_SIZE || ly < 0 || ly > GIZMO_SIZE) return false;
  _ndc.set((lx / GIZMO_SIZE) * 2 - 1, 1 - (ly / GIZMO_SIZE) * 2);
  return true;
}

function ballUnder(clientX: number, clientY: number): THREE.Mesh | null {
  if (!ndcInGizmo(clientX, clientY)) return null;
  gizmo.updateMatrixWorld(true);
  _raycaster.setFromCamera(_ndc, gizmoCamera);
  // Nearest ball that's front-facing — so a back ball peeking out from behind
  // the one pointing at you is never the hit (you grab the front one).
  const hits = _raycaster.intersectObjects(balls, false);
  for (const h of hits) {
    const m = h.object as THREE.Mesh;
    if (m.userData.front !== false) return m;
  }
  return null;
}

// World axis to look down if an axis ball is under the cursor, else null.
export function pickGizmoAxis(
  clientX: number,
  clientY: number,
): THREE.Vector3 | null {
  const b = ballUnder(clientX, clientY);
  return b ? (b.userData.axisDir as THREE.Vector3).clone() : null;
}

// Highlight the ball under the cursor (enlarges it). Pass an off-screen point
// (e.g. -1,-1) to clear.
export function setGizmoHover(clientX: number, clientY: number): void {
  // Only the ball scale is set here; label visibility (incl. the hover-only
  // negative labels) is reconciled every frame in applyFacing using `hovered`.
  const b = isInsideGizmo(clientX, clientY) ? ballUnder(clientX, clientY) : null;
  if (b === hovered) return;
  if (hovered) hovered.scale.setScalar(1);
  hovered = b;
  if (hovered) hovered.scale.setScalar(HOVER_SCALE);
}

// Render the gizmo overlay in the bottom-right corner, mirroring `mainCamera`'s
// orientation. Caller gates this to desktop-only (skip in XR).
export function renderGizmoOverlay(
  renderer: THREE.WebGLRenderer,
  mainCamera: THREE.Camera,
): void {
  gizmo.quaternion.copy(mainCamera.quaternion).invert();
  applyFacing(); // fade/occlude back balls, toggle labels, set pickability
  const prevAutoClear = renderer.autoClear;
  renderer.autoClear = false;
  renderer.clearDepth();
  renderer.setViewport(
    window.innerWidth - GIZMO_SIZE,
    0, // WebGL viewport y counts from the bottom of the canvas
    GIZMO_SIZE,
    GIZMO_SIZE,
  );
  renderer.render(gizmoScene, gizmoCamera);
  renderer.setViewport(0, 0, window.innerWidth, window.innerHeight);
  renderer.autoClear = prevAutoClear;
}
